import { useState } from 'react';

import { Button } from '@/design-system';
import type { ReleasedReport } from '@/entities/report';
import { formatDayLong } from '@/shared/lib';

import { copyText } from '../browser';
import { reportCopy } from '../copy';
import { ReportStateCard } from './ReportStateCard';

export interface ReleasedVersionNoticeProps {
    /** De versie die de koper heeft. */
    released: ReleasedReport;
    /** De nieuwere versie op dit scherm, die nog niet naar de klant ging. */
    newestVersion: number;
    onOpenClientPage: () => void;
}

/**
 * Na "Nieuwe versie maken" staat hier een concept, maar de koper heeft nog de
 * verstuurde versie - met zijn eigen link. Dit blok houdt die bereikbaar: welke
 * versie hij heeft, tot wanneer zijn link werkt, de link zelf en de klantpagina.
 */
export function ReleasedVersionNotice({
    released,
    newestVersion,
    onOpenClientPage,
}: ReleasedVersionNoticeProps) {
    const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
    const link = released.link;
    const url = link?.url ?? null;

    return (
        <ReportStateCard
            tone="default"
            title={reportCopy.releasedTitle(released.version)}
            action={
                <>
                    <Button variant="outline" size="md" onClick={onOpenClientPage}>
                        {reportCopy.releasedClientPage(released.version)}
                    </Button>
                    {url ? (
                        <Button
                            variant="outline"
                            size="md"
                            onClick={async () =>
                                setCopy((await copyText(url)) ? 'copied' : 'failed')
                            }
                        >
                            {copy === 'copied' ? reportCopy.copied : reportCopy.copyLink}
                        </Button>
                    ) : null}
                </>
            }
        >
            <p className="m-0">
                {link
                    ? reportCopy.releasedBody(
                          released.version,
                          newestVersion,
                          formatDayLong(link.validUntil),
                      )
                    : reportCopy.releasedWithoutLink(released.version, newestVersion)}
            </p>
            {url ? (
                <p className="m-0 mt-2 font-[family-name:var(--font-mono)] text-[12px] leading-[1.45] break-all text-[var(--text-primary)] select-all">
                    {url}
                </p>
            ) : null}
            {copy === 'failed' ? (
                <p
                    role="alert"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--warn-ink)]"
                >
                    {reportCopy.copyFailed}
                </p>
            ) : null}
        </ReportStateCard>
    );
}
